import { useState, useEffect } from "react";
import API from "../api";

export default function HR() {

  const [search, setSearch] = useState("");
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [form, setForm] = useState({
    id: null,
    name: "",
    role: "",
    email: "",
    status: "active"
  });

  /* ===============================
     FETCH EMPLOYEES
  =============================== */
  const fetchEmployees = async () => {
    try {
      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      const res = await fetch(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json();
      setEmployees(data.data || []);

    } catch (err) {
      console.error("❌ Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  /* ===============================
     CREATE / UPDATE
  =============================== */
  const handleSubmit = async () => {
    try {
      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      const url = editMode
        ? `${API}/employees/${form.id}`
        : `${API}/employees`;

      const method = editMode ? "PUT" : "POST";

      await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });

      fetchEmployees();
      closeModal();

    } catch (err) {
      console.error(err);
    }
  };

  /* ===============================
     DELETE
  =============================== */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete employee?")) return;

    try {
      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      await fetch(`${API}/employees/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      setEmployees(prev => prev.filter(e => e.id !== id));

    } catch (err) {
      console.error(err);
    }
  };

  /* ===============================
     EDIT
  =============================== */
  const handleEdit = (emp) => {
    setForm(emp);
    setEditMode(true);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditMode(false);
    setForm({
      id: null,
      name: "",
      role: "",
      email: "",
      status: "active"
    });
  };

  /* ===============================
     FILTER
  =============================== */
  const filteredEmployees = employees.filter((emp) =>
    emp.name.toLowerCase().includes(search.toLowerCase())
  );

  /* ===============================
     KPI
  =============================== */
  const normalize = (s) => (s || "").toLowerCase();

  const total = employees.length;
  const active = employees.filter(e => normalize(e.status) === "active").length;
  const onLeave = employees.filter(e => normalize(e.status) === "on_leave").length;

  /* ===============================
     STATUS COLOR
  =============================== */
  const getStatusColor = (status) => {
    const s = normalize(status);
    if (s === "active") return "bg-green-100 text-green-700";
    return "bg-yellow-100 text-yellow-700";
  };

  return (
    <div className="space-y-6">

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold">HR Management</h2>
          <p className="text-gray-500">
            Manage employees and team performance
          </p>
        </div>

        <button
          onClick={() => setShowModal(true)}
          className="bg-green-600 text-white px-4 py-2 rounded-lg"
        >
          Add Employee
        </button>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-3 gap-6">

        <div className="bg-white p-6 rounded-xl border">
          <h4>Total Employees</h4>
          <p className="text-2xl">{total}</p>
        </div>

        <div className="bg-white p-6 rounded-xl border text-green-600">
          <h4>Active</h4>
          <p className="text-2xl">{active}</p>
        </div>

        <div className="bg-white p-6 rounded-xl border text-yellow-600">
          <h4>On Leave</h4>
          <p className="text-2xl">{onLeave}</p>
        </div>

      </div>

      {/* SEARCH */}
      <input
        className="w-full p-3 border rounded-lg"
        placeholder="Search employees..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* TABLE */}
      <div className="bg-white border rounded-xl overflow-x-auto">

        {loading ? (
          <div className="p-6 text-center">Loading...</div>
        ) : filteredEmployees.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No employees found
          </div>
        ) : (
          <table className="w-full text-sm">

            <thead className="bg-gray-100">
              <tr>
                <th className="p-4">Name</th>
                <th>Role</th>
                <th>Email</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {filteredEmployees.map((emp) => (
                <tr key={emp.id} className="border-t hover:bg-gray-50">

                  <td className="p-4 font-medium">{emp.name}</td>
                  <td>{emp.role}</td>
                  <td>{emp.email}</td>

                  <td>
                    <span className={`px-2 py-1 text-xs rounded ${getStatusColor(emp.status)}`}>
                      {emp.status.replace("_", " ")}
                    </span>
                  </td>

                  {/* ACTION */}
                  <td className="flex gap-2 p-4">
                    <button
                      onClick={() => handleEdit(emp)}
                      className="text-blue-600 text-sm"
                    >
                      Edit
                    </button>

                    <button
                      onClick={() => handleDelete(emp.id)}
                      className="text-red-600 text-sm"
                    >
                      Delete
                    </button>
                  </td>

                </tr>
              ))}
            </tbody>

          </table>
        )}

      </div>

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center">
          <div className="bg-white p-6 rounded-xl w-96 space-y-3">

            <h3 className="font-bold">
              {editMode ? "Edit Employee" : "Add Employee"}
            </h3>

            <input
              placeholder="Name"
              className="w-full p-2 border"
              value={form.name}
              onChange={(e)=>setForm({...form,name:e.target.value})}
            />

            <input
              placeholder="Role"
              className="w-full p-2 border"
              value={form.role}
              onChange={(e)=>setForm({...form,role:e.target.value})}
            />

            <input
              placeholder="Email"
              className="w-full p-2 border"
              value={form.email}
              onChange={(e)=>setForm({...form,email:e.target.value})}
            />

            <select
              className="w-full p-2 border"
              value={form.status}
              onChange={(e)=>setForm({...form,status:e.target.value})}
            >
              <option value="active">Active</option>
              <option value="on_leave">On Leave</option>
            </select>

            <div className="flex justify-end gap-2">
              <button onClick={closeModal}>Cancel</button>

              <button
                onClick={handleSubmit}
                className="bg-green-600 text-white px-4 py-2 rounded"
              >
                {editMode ? "Update" : "Save"}
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}